import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Chip,
  useTheme,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DevicesIcon from '@mui/icons-material/Devices';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import api from '../api';

interface DeviceType {
  id: number;
  name: string;
  deviceCount: number;
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Device types state
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<DeviceType | null>(null);
  const [typeName, setTypeName] = useState('');
  const [typeNameError, setTypeNameError] = useState('');
  const [typeSubmitting, setTypeSubmitting] = useState(false);
  const [typeDeleteOpen, setTypeDeleteOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<DeviceType | null>(null);

  const fetchDeviceTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      const { data } = await api.get<DeviceType[]>('/device-types');
      setDeviceTypes(data);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    } finally {
      setTypesLoading(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    fetchDeviceTypes();
  }, [fetchDeviceTypes]);

  const handleClearData = async () => {
    setClearing(true);
    try {
      await api.delete('/settings/clear-data');
      enqueueSnackbar(t('settings.clearedSnackbar'), { variant: 'success' });
      setConfirmOpen(false);
      fetchDeviceTypes();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    } finally {
      setClearing(false);
    }
  };

  const openAddType = () => {
    setEditingType(null);
    setTypeName('');
    setTypeNameError('');
    setTypeDialogOpen(true);
  };

  const openEditType = (dt: DeviceType) => {
    setEditingType(dt);
    setTypeName(dt.name);
    setTypeNameError('');
    setTypeDialogOpen(true);
  };

  const handleTypeSubmit = async () => {
    const trimmed = typeName.trim();
    if (!trimmed) {
      setTypeNameError(t('settings.deviceTypesNameRequired'));
      return;
    }
    setTypeSubmitting(true);
    setTypeNameError('');
    try {
      if (editingType) {
        await api.put(`/device-types/${editingType.id}`, { name: trimmed });
        enqueueSnackbar(t('settings.snackbarTypeUpdated'), { variant: 'success' });
      } else {
        await api.post('/device-types', { name: trimmed });
        enqueueSnackbar(t('settings.snackbarTypeCreated'), { variant: 'success' });
      }
      setTypeDialogOpen(false);
      fetchDeviceTypes();
    } catch (err: any) {
      const msg = err.response?.data?.error;
      if (msg?.includes('already exists')) {
        setTypeNameError(t('settings.deviceTypesAlreadyExists'));
      } else {
        enqueueSnackbar(msg || t('common.error'), { variant: 'error' });
      }
    } finally {
      setTypeSubmitting(false);
    }
  };

  const openDeleteType = (dt: DeviceType) => {
    setTypeToDelete(dt);
    setTypeDeleteOpen(true);
  };

  const handleDeleteType = async () => {
    if (!typeToDelete) return;
    try {
      await api.delete(`/device-types/${typeToDelete.id}`);
      enqueueSnackbar(t('settings.snackbarTypeDeleted'), { variant: 'success' });
      setTypeDeleteOpen(false);
      setTypeToDelete(null);
      fetchDeviceTypes();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 4,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            background: isDark
              ? 'linear-gradient(135deg, #8b8bff 20%, #5ee7f1 80%)'
              : 'linear-gradient(135deg, #5b5bf6 20%, #22c3d1 80%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            boxShadow: isDark
              ? '0 8px 20px -8px rgba(139,139,255,0.7)'
              : '0 8px 20px -8px rgba(91,91,246,0.5)',
          }}
        >
          <SettingsIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
            {t('settings.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
            {t('settings.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Device types management */}
      <Card sx={{ mb: 4, borderRadius: 1.5, border: '1px solid var(--border)' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: isDark
                    ? 'linear-gradient(135deg, #8b8bff, #5ee7f1)'
                    : 'linear-gradient(135deg, #5b5bf6, #22c3d1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: '#fff',
                  boxShadow: isDark
                    ? '0 10px 25px -10px rgba(139,139,255,0.5)'
                    : '0 10px 25px -10px rgba(91,91,246,0.4)',
                }}
              >
                <DevicesIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {t('settings.deviceTypesTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-muted)', maxWidth: 560 }}>
                  {t('settings.deviceTypesDesc')}
                </Typography>
              </Box>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddType}>
              {t('settings.deviceTypesAdd')}
            </Button>
          </Box>

          {typesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : deviceTypes.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'var(--text-muted)', textAlign: 'center', py: 4 }}>
              {t('settings.deviceTypesEmpty')}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, border: '1px solid var(--border)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('settings.deviceTypesName')}</TableCell>
                    <TableCell>{t('settings.deviceTypesDevices')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deviceTypes.map((dt) => (
                    <TableRow key={dt.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{dt.name}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={dt.deviceCount}
                          sx={{
                            background: isDark
                              ? 'rgba(139,139,255,0.15)'
                              : 'rgba(91,91,246,0.12)',
                            color: 'inherit',
                            border: 'none',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEditType(dt)} title={t('common.edit')}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => openDeleteType(dt)} title={t('common.delete')}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Danger zone card */}
      <Card
        sx={{
          border: `1px solid ${isDark ? 'rgba(248,113,113,0.3)' : 'rgba(239,68,68,0.25)'}`,
          background: isDark
            ? 'linear-gradient(135deg, rgba(248,113,113,0.06), rgba(239,68,68,0.02))'
            : 'linear-gradient(135deg, rgba(239,68,68,0.04), rgba(220,38,38,0.01))',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: isDark
                  ? 'linear-gradient(135deg, #f87171, #ef4444)'
                  : 'linear-gradient(135deg, #ef4444, #dc2626)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: '#fff',
                boxShadow: isDark
                  ? '0 10px 25px -10px rgba(248,113,113,0.5)'
                  : '0 10px 25px -10px rgba(239,68,68,0.4)',
              }}
            >
              <DeleteSweepIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {t('settings.clearDataTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-muted)', mb: 2.5, maxWidth: 560 }}>
                {t('settings.clearDataDesc')}
              </Typography>
              <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                sx={{
                  borderRadius: 1.5,
                  mb: 2.5,
                  background: isDark ? 'rgba(251,191,36,0.08)' : 'rgba(251,191,36,0.06)',
                  border: `1px solid ${isDark ? 'rgba(251,191,36,0.25)' : 'rgba(251,191,36,0.2)'}`,
                }}
              >
                {t('settings.clearDataWarning')}
              </Alert>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={() => setConfirmOpen(true)}
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #f87171, #ef4444)'
                    : 'linear-gradient(135deg, #ef4444, #dc2626)',
                  boxShadow: isDark
                    ? '0 6px 20px -6px rgba(248,113,113,0.5)'
                    : '0 6px 20px -6px rgba(239,68,68,0.4)',
                  '&:hover': {
                    background: isDark
                      ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                      : 'linear-gradient(135deg, #dc2626, #b91c1c)',
                  },
                }}
              >
                {t('settings.clearDataButton')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Device type add/edit dialog */}
      <Dialog open={typeDialogOpen} onClose={() => setTypeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingType ? t('settings.deviceTypesEdit') : t('settings.deviceTypesAdd')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            label={t('settings.deviceTypesName')}
            value={typeName}
            onChange={(e) => setTypeName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleTypeSubmit(); }}
            error={!!typeNameError}
            helperText={typeNameError}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTypeDialogOpen(false)} disabled={typeSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleTypeSubmit} disabled={typeSubmitting}>
            {typeSubmitting ? <CircularProgress size={20} color="inherit" /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Device type delete confirmation */}
      <Dialog open={typeDeleteOpen} onClose={() => setTypeDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DeleteIcon sx={{ color: isDark ? '#f87171' : '#dc2626' }} />
          {t('settings.deviceTypesDelete')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--text-muted)', mb: 2 }}>
            {t('settings.deviceTypesDeleteConfirm', { name: typeToDelete?.name })}
          </Typography>
          {(typeToDelete?.deviceCount ?? 0) > 0 && (
            <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
              {t('settings.deviceTypesDeleteInUse', { count: typeToDelete?.deviceCount })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTypeDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteType}
            disabled={(typeToDelete?.deviceCount ?? 0) > 0}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmberIcon sx={{ color: isDark ? '#fbbf24' : '#d97706' }} />
          {t('settings.confirmTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--text-muted)', mb: 2 }}>
            {t('settings.confirmDesc')}
          </Typography>
          <Alert severity="error" sx={{ borderRadius: 1.5 }}>
            {t('settings.confirmWarning')}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={clearing}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleClearData}
            disabled={clearing}
            autoFocus
          >
            {clearing ? <CircularProgress size={20} color="inherit" /> : t('settings.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
